import Button from 'components/inputs/button'
import { Toggle } from 'components/ui'
import classNames from 'helpers/class-names'
import { useMemo, useState } from 'react'
import useSWR from 'swr'

interface Data {
  monthly: number
  yearly: number
}

const UpgradeForm = () => {
  const [isYearly, setIsYearly] = useState<boolean>(true)
  const { data } = useSWR<Data>('/api/pricing')

  const pricing: string = useMemo(() => {
    if (!data) {
      return
    }

    const amount = (isYearly ? data.yearly : data.monthly) / 100
    return amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  }, [data, isYearly])

  const savings = useMemo(() => {
    if (!data) {
      return
    }

    let savings

    const monthlyPerYear = data.monthly * 12
    if (isYearly && data.yearly < monthlyPerYear) {
      savings = monthlyPerYear - data.yearly
    }

    if (!isYearly && monthlyPerYear < data.yearly) {
      savings = data.yearly - monthlyPerYear
    }

    if (!savings) {
      return
    }

    savings = savings / 100

    return savings.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  }, [data, isYearly])

  return (
    <div className="mt-2 flex flex-col gap-4 border-t border-info-lighten/25 pt-6 tracking-normal">
      <div className="flex flex-col gap-4">
        <Toggle
          options={['Monthly', 'Yearly']}
          isRight={isYearly}
          setIsRight={setIsYearly}
          switchClass="w-40 outline-info-lighten/60 bg-info-lighten/60"
          toggleClass="w-20"
          optionClass="w-20 text-gray"
        />
        {pricing && (
          <>
            <div className="flex flex-row items-center gap-1.5 text-3xl font-bold">
              ${pricing}
              <span className="ml-0.5 text-xs font-normal italic leading-3 text-gray-700/75">
                per
                <br />
                {isYearly ? 'year' : 'month'}
              </span>
            </div>
          </>
        )}
      </div>
      <div
        className={classNames('flex w-64 flex-col gap-2 pt-2', {
          '-mb-6': savings,
        })}>
        <Button
          secondary
          asLink
          href={`/api/upgrade?interval=${isYearly ? 'yearly' : 'monthly'}`}
          target="_blank"
          className="inline-block border-none text-gray-700 transition-all duration-300 hover:scale-105 active:scale-95">
          Buy {isYearly ? 'Yearly' : 'Monthly'}
        </Button>
        {Boolean(savings) && (
          <div className="text-xs text-white">
            Save <strong>${savings}</strong> a year!
          </div>
        )}
      </div>
    </div>
  )
}

export default UpgradeForm
